/**
 * API请求工具
 * 封装了基本的API请求方法，支持Protocol Buffers格式
 */

import { getToken, getRefreshToken, refreshToken, clearAuth } from './authService';
import { encodeMessage, decodeMessage } from '../utils/protobufHelper';
import { isLoggedIn } from './kratosService';
import { ensureOAuth2Token } from './tokenSyncService';
import { getAuthorizeUrl } from './oauth2Service';
// import { message } from 'antd'; // 移除antd原生message
// 统一用 window.message 代理，由入口处挂载 useAppMessage.message

// 内嵌登录弹窗触发器（由 App.js 初始化时注入）
let showEmbeddedLoginModal = null;

/**
 * 注册内嵌登录弹窗触发器
 * @param {Function} modalTrigger - 弹窗触发函数
 */
export const registerEmbeddedLoginTrigger = (modalTrigger) => {
  showEmbeddedLoginModal = modalTrigger;
  console.log('✅ Embedded login trigger registered');
};

// 辅助函数：打开登录弹窗（实际上是跳转到 OneAuth）
let isLoginPopupOpen = false;
const openLoginPopup = async (redirectUrl) => {
  if (isLoginPopupOpen) {
    return;
  }
  
  isLoginPopupOpen = true;
  // 3秒后重置标志，防止用户关闭窗口后无法再次触发
  setTimeout(() => {
    isLoginPopupOpen = false;
  }, 3000);

  if (showEmbeddedLoginModal) {
    showEmbeddedLoginModal();
  } else {
    // 降级方案：直接跳转到 OneAuth
    
    // 关闭已存在的登录窗口
    if (window.loginPopup && !window.loginPopup.closed) {
      window.loginPopup.close();
    }

    // 获取 Hydra 授权 URL
    const authorizeUrl = await getAuthorizeUrl(redirectUrl);

    // 计算屏幕中心位置
    const width = 900;
    const height = 648;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`;

    window.loginPopup = window.open(authorizeUrl, 'oauth2_login_popup', features);
  }
};

// 检查BASE_URL的配置

// 基础URL，可以根据环境变量配置
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
// 是否使用Protocol Buffers格式
// 注意：实际项目中应该根据后端API要求设置
// 这里提供一个开关，方便在开发阶段切换
const USE_PROTOBUF = false;

// Token 刷新状态管理
let isRefreshing = false;
let refreshPromise = null;

/**
 * 尝试刷新 Token
 * @returns {Promise<boolean>} - 是否刷新成功
 */
const tryRefreshToken = async () => {
  // 防止并发刷新
  if (isRefreshing) {
    console.log('🔄 Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  const refresh_token = getRefreshToken();

  if (!refresh_token) {
    console.warn('⚠️ No refresh token available, cannot refresh');
    return false;
  }

  console.log('🔄 Attempting to refresh access token...');
  isRefreshing = true;

  refreshPromise = refreshToken()
    .then(() => {
      console.log('✅ Token refresh successful');
      isRefreshing = false;
      refreshPromise = null;
      return true;
    })
    .catch((error) => {
      console.error('❌ Token refresh failed:', error);
      isRefreshing = false;
      refreshPromise = null;
      return false;
    });

  return refreshPromise;
};

/**
 * 处理token过期或401未授权
 * 先检查 Kratos session，然后尝试获取 OAuth2 token，最后尝试刷新 token，都失败才弹登录框
 */
const handleTokenExpired = async (isGetRequest = false) => {
  try {
    // 0. 先检查本地是否有 token，如果没有说明已登出或从未登录，无需尝试恢复
    if (!getToken() && !getRefreshToken()) {
      console.log('ℹ️ No local tokens found, skipping token recovery (user logged out or never logged in)');
      return false;
    }

    // 1. 检查是否已有 Kratos session
    console.log('🔍 Checking if user already has valid Kratos session...');
    let hasSession = false;
    try {
      hasSession = await isLoggedIn();
      if (hasSession) {
        console.log('✅ User has valid Kratos session');
      }
    } catch (error) {
      console.log('ℹ️ Could not check Kratos session:', error.message);
    }

    // 1. 如果有 Kratos session 但没有/无效的 OAuth2 token，尝试获取新 token
    if (hasSession) {
      const currentToken = getToken();
      console.log('🔍 Current OAuth2 token:', currentToken ? 'exists' : 'missing');
      
      // 强制刷新 token（因为当前 token 已经被 backend 拒绝了）
      console.log('🔄 Attempting to get fresh OAuth2 token (force refresh)...');
      const tokenAcquired = await ensureOAuth2Token(true); // 传 true 强制刷新
      
      if (tokenAcquired) {
        console.log('✅ OAuth2 token acquired successfully, request can be retried');
        return true; // Token 获取成功，可以重试请求
      } else {
        console.warn('⚠️ Failed to acquire OAuth2 token despite having Kratos session');
      }
    }

    // 2. 尝试刷新 token
    const refreshed = await tryRefreshToken();

    if (refreshed) {
      console.log('✅ Token refreshed successfully, continuing...');
      return true; // 刷新成功，可以继续
    }

    // 3. 所有尝试都失败，弹出登录小窗
    console.warn('⚠️ All token recovery attempts failed, showing login popup...');
    clearAuth();
    
    // 如果是静默失败模式（例如首页的后台请求），则不弹窗
    const isPublicPath = window.location.pathname === '/' || window.location.pathname.startsWith('/explore');
    // 检查是否是后台自动请求（这里简单通过 document.visibilityState 或者假定 GET 请求在 Public Page 不弹窗太激进）
    // 更好的方式是：如果页面是 Public 的，且用户没有进行交互（click/submit），则不弹窗。
    // 但 API 层很难知道用户是否交互。
    // 我们暂时依赖 AppSidebar 和 NotificationButton 的修复。如果还有漏网之鱼，这里做一个兜底：
    // 如果没有 Token 且在 Public Page，通常意味着用户本身就未登录，此时 401 是预期的，不应弹窗。
    // 只有当用户持有 Token（但过期了）且刷新失败时，或者用户明确点击了什么导致 401，才弹窗。
    // 但 handleTokenExpired 只有在 refresh token 失败或者根本没有 refresh token 时才会走到这里。
    
    // 兜底逻辑：如果是在 Public 页面，且根本没有 Token (getRefreshToken() 返回 null)，则不弹窗，直接返回 false
    // 这样可以让页面保持未登录状态显示，而不是强制弹窗。
    if (isPublicPath && isGetRequest) {
       console.log('ℹ️ Public page with no auth, suppressing login popup for 401');
       return false;
    }

    // 监听登录成功消息
    const loginHandler = (event) => {
      if (event.data?.type === 'LOGIN_SUCCESS') {
        console.log('✅ Received LOGIN_SUCCESS message from popup, reloading...');
        window.removeEventListener('message', loginHandler);
        // 刷新页面以恢复状态
        window.location.reload();
      }
    };
    window.addEventListener('message', loginHandler);

    // 打开登录小窗
    openLoginPopup(window.location.href);

    return false;
  } catch (error) {
    console.error('❌ 处理 token 过期失败:', error);
    // 即使失败也清除并弹出登录框或重定向
    clearAuth();
    
    // 允许 /explore 下的所有子页面（如详情页）在未登录时静默失败
    const isPublicPath = window.location.pathname === '/' || window.location.pathname.startsWith('/explore');

    // 兜底逻辑：如果是在 Public 页面，且是 GET 请求，则不弹窗，直接返回 false
    if (isPublicPath && isGetRequest) {
       console.log('ℹ️ Public page GET request (error handler), suppressing login popup');
       return false;
    }
    
    // 监听登录成功消息
    const loginHandler = (event) => {
      if (event.data?.type === 'LOGIN_SUCCESS') {
        window.location.reload();
      }
    };
    window.addEventListener('message', loginHandler);
    
    openLoginPopup(window.location.href);
    return false;
  }
};

/**
 * 处理API响应
 * @param {Response} response - fetch API的响应对象
 * @param {string} responseType - 响应消息类型名称
 * @param {Object} options - 响应选项
 * @returns {Promise} - 处理后的响应数据
 */
const handleResponse = async (response, responseType, options = {}) => {
  // 1. 首先检查HTTP状态码
  if (!response.ok) {
    console.log('🔴 API Error:', response.status, response.url);
    // 特殊处理 401 未授权
    if (response.status === 401) {
      console.warn('⚠️ HTTP 401 Unauthorized detected');
      
      // 如果当前是首页或探索页，且是 GET 请求（获取数据），可能是公共数据获取失败，或者是后台轮询
      // 我们可以选择不弹出登录框，而是静默失败（让前端显示空状态）
      // 但如果是用户的主动操作（POST/PUT/DELETE），则必须弹窗
      // 这里简单判断：如果是 GET 请求且在 public paths 下，尝试静默处理
      const isGetRequest = options.method === 'GET';
      
      // 注意：某些 GET 请求（如获取用户信息 /me）即使在首页也应该失败，但不一定非要弹窗
      // 如果我们已经在前端组件层（如 AppSidebar, NotificationButton）做了 token 检查，
      // 这里的拦截器就是最后的防线。
      
      // 策略：只有当 "handleTokenExpired" 决定需要弹窗时才弹窗
      // 我们修改 handleTokenExpired 逻辑，或者在这里做前置判断
      
      // 尝试刷新 token 或弹出登录框
      // 传入 isGetRequest 标志，用于判断是否静默失败
      const refreshed = await handleTokenExpired(isGetRequest);

      if (refreshed) {
        // Token 刷新成功，抛出特殊错误让外层重试
        const error = new Error('Token refreshed, please retry');
        error.shouldRetry = true;
        throw error;
      } else {
        // 登录框已弹出，抛出错误但不显示消息
        const error = new Error('Authentication required');
        error.isAuthError = true;
        throw error;
      }
    }

    // HTTP状态码不是2xx，显示错误提示并抛出错误
    const errorMessage = `请求失败: HTTP ${response.status}`;
    window.message?.error?.(errorMessage); // 使用全局message

    try {
      if (USE_PROTOBUF) {
        const errorBuffer = await response.text();
        const errorData = decodeMessage('Error', errorBuffer);
        throw new Error(errorData.message || errorMessage);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorMessage);
      }
    } catch (error) {
      // 如果是认证错误，不要覆盖
      if (error.isAuthError) {
        throw error;
      }
      throw new Error(errorMessage);
    }
  }

  // 2. 检查是否需要返回Blob（文件下载）
  if (options.responseType === 'blob') {
    return await response.blob();
  }

  // 3. HTTP状态码为200，解析响应数据
  let responseData;

  if (USE_PROTOBUF) {
    const buffer = await response.text();
    responseData = decodeMessage(responseType, buffer);
  } else {
    // 检查内容类型，如果是JSON则解析为JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      // 如果不是JSON格式，直接返回文本内容
      return await response.text();
    }
  }

  // 4. 检查业务层包装的响应格式
  if (responseData && typeof responseData === 'object') {
    // 检查是否有业务状态码
    if ('code' in responseData) {
      const { code, data, message: businessMessage } = responseData;

      // 检查成功状态码：0 表示一般成功，201 表示创建成功
      if (code === 0 || code === 201) {
        // 业务code为0或201，处理 v2 API 的 data 层包装
        return data;
      } else if (code === 2002) {
        // code为2002表示登录token过期
        // 抛出特殊错误，让外层处理器捕获并刷新 token 后重试
        const error = new Error(businessMessage || '登录已过期');
        error.code = 2002;
        error.isTokenExpired = true;
        throw error;
      } else {
        // 其他业务code，显示错误消息并抛出错误
        const errorMessage = businessMessage || `业务错误: code ${code}`;
        window.message?.error?.(errorMessage); // 使用全局message
        throw new Error(errorMessage);
      }
    }
  }

  // 5. 如果没有业务层包装，直接返回原始数据
  return responseData;
};

/**
 * 构建请求选项
 * @param {string} method - HTTP方法
 * @param {Object|FormData} data - 请求数据
 * @param {string} requestType - 请求消息类型名称
 * @param {Object} options - 额外的请求选项
 * @returns {Object} - 完整的请求选项
 */
const buildRequestOptions = (method, data, requestType, options = {}) => {
  // 获取认证Token
  const token = getToken();

  // 检查是否是文件上传（FormData）
  const isFormData = data instanceof FormData;

  // 构建基本请求选项
  const requestOptions = {
    method,
    headers: {
      // 如果是FormData，不设置Content-Type，让浏览器自动设置
      ...(isFormData
        ? {}
        : USE_PROTOBUF
        ? { 'Content-Type': 'application/x-protobuf' }
        : { 'Content-Type': 'application/json' }),
      // 添加认证头
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    // 添加跨域支持
    mode: 'cors',
    credentials: 'include',
    ...options,
  };

  // 添加请求体
  if (data) {
    if (isFormData) {
      // 如果是FormData，直接使用
      requestOptions.body = data;
    } else if (USE_PROTOBUF) {
      // 使用Protocol Buffers格式
      requestOptions.body = encodeMessage(requestType, data);
    } else {
      // 使用JSON格式
      requestOptions.body = JSON.stringify(data);
    }
  }

  return requestOptions;
};

/**
 * 包装请求，支持自动 token 刷新和重试
 * @param {Function} requestFn - 实际的请求函数
 * @param {string} method - 请求方法 (GET, POST, etc.)
 * @returns {Promise} - 响应数据
 */
const withTokenRefresh = async (requestFn, method = 'GET') => {
  try {
    return await requestFn();
  } catch (error) {
    // 检查是否是 token 过期错误
    if (error.isTokenExpired && error.code === 2002) {
      console.log('🔄 Token expired, attempting refresh and retry...');

      // 尝试刷新 token
      const refreshed = await handleTokenExpired(method === 'GET');

      if (refreshed) {
        // Token 刷新成功，重试请求
        console.log('🔄 Retrying request with new token...');
        try {
          return await requestFn();
        } catch (retryError) {
          console.error('❌ Retry failed after token refresh:', retryError);
          throw retryError;
        }
      } else {
        // Token 刷新失败，抛出原始错误
        throw error;
      }
    }
    // 非 token 过期错误，直接抛出
    throw error;
  }
};

/**
 * API请求方法
 */
const api = {
  /**
   * GET请求
   * @param {string} endpoint - API端点
   * @param {Object} options - 请求选项
   * @param {string} responseType - 响应消息类型名称
   * @returns {Promise} - 响应数据
   */
  get: async (endpoint, options = {}, responseType = '') => {
    return withTokenRefresh(async () => {
      // 构建URL查询参数
      let url = `${BASE_URL}${endpoint}`;
      if (options.params) {
        const queryParams = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
              queryParams.append(key, JSON.stringify(value));
            } else {
              queryParams.append(key, value);
            }
          }
        });
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      // 发送请求
      const response = await fetch(
        url,
        buildRequestOptions('GET', null, '', options)
      );
      return handleResponse(response, responseType, { ...options, method: 'GET' });
    });
  },

  /**
   * POST请求
   * @param {string} endpoint - API端点
   * @param {Object} data - 请求数据
   * @param {string} requestType - 请求消息类型名称
   * @param {string} responseType - 响应消息类型名称
   * @param {Object} options - 请求选项
   * @returns {Promise} - 响应数据
   */
  post: async (
    endpoint,
    data,
    requestType = '',
    responseType = '',
    options = {}
  ) => {
    return withTokenRefresh(async () => {
      const url = `${BASE_URL}${endpoint}`;
      const response = await fetch(
        url,
        buildRequestOptions('POST', data, requestType, options)
      );
      return handleResponse(response, responseType, { ...options, method: 'POST' });
    });
  },

  /**
   * PUT请求
   * @param {string} endpoint - API端点
   * @param {Object} data - 请求数据
   * @param {string} requestType - 请求消息类型名称
   * @param {string} responseType - 响应消息类型名称
   * @param {Object} options - 请求选项
   * @returns {Promise} - 响应数据
   */
  put: async (
    endpoint,
    data,
    requestType = '',
    responseType = '',
    options = {}
  ) => {
    return withTokenRefresh(async () => {
      const url = `${BASE_URL}${endpoint}`;
      const response = await fetch(
        url,
        buildRequestOptions('PUT', data, requestType, options)
      );
      return handleResponse(response, responseType, { ...options, method: 'PUT' });
    });
  },

  /**
   * DELETE请求
   * @param {string} endpoint - API端点
   * @param {Object} data - 请求数据
   * @param {string} requestType - 请求消息类型名称
   * @param {string} responseType - 响应消息类型名称
   * @param {Object} options - 请求选项
   * @returns {Promise} - 响应数据
   */
  delete: async (
    endpoint,
    data = null,
    requestType = '',
    responseType = '',
    options = {}
  ) => {
    return withTokenRefresh(async () => {
      const url = `${BASE_URL}${endpoint}`;
      const response = await fetch(
        url,
        buildRequestOptions('DELETE', data, requestType, options)
      );
      return handleResponse(response, responseType, { ...options, method: 'DELETE' });
    });
  },

  /**
   * 文件上传请求
   * @param {string} endpoint - API端点
   * @param {FormData} formData - FormData对象
   * @param {string} responseType - 响应消息类型名称
   * @param {Object} options - 请求选项
   * @returns {Promise} - 响应数据
   */
  upload: async (endpoint, formData, responseType = '', options = {}) => {
    return withTokenRefresh(async () => {
      const url = `${BASE_URL}${endpoint}`;
      const response = await fetch(
        url,
        buildRequestOptions('POST', formData, '', options)
      );
      return handleResponse(response, responseType, { ...options, method: 'POST' });
    }, 'POST');
  },
};

export default api;
